<template>
  <div ref="mapContainer">
    <svg
      ref="map"
      viewBox="0 0 1920 1080">
      <defs ref="mapColorPatterns" />
      <g
        ref="mapInsets"
        class="state-insets" />
      <g ref="mapFeatures" />
      <g ref="mapLabels" />
    </svg>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import d3 from "@/plugins/d3";

import { capitalize } from "@/filters";
import { getCSSVarHex, hexToRGB, debounceAF } from "@/helpers/utils.js";
import MapMixin from "@/mixins/map";

const mixins = [MapMixin];

const props = {
  office: {
    type: String,
    required: false,
  },
  results: {
    type: Array,
    required: true,
  },
  filterState: {
    type: String,
    required: false,
  },
  geoType: {
    type: String,
    required: true,
  },
  loading: {
    type: Boolean,
    required: false,
  },
  showEcVotes: {
    type: Boolean,
    required: false,
  },
  isSplitVoteState: {
    type: Boolean,
    required: false,
  },
  showSplitVote: {
    type: Boolean,
    required: false,
  },
};

const computed = {
  ...mapGetters(["colors"]),
  id() {
    return "nationwide-map";
  },
  geojson() {
    if (["state_senate", "state_house"].includes(this.office)) {
      return this.topojson(this.geoType, this.filterState)();
    }
    let data = this.topojson(this.geoType)();
    if (this.filterState) {
      let filterState = this.filterState;
      if (filterState.includes("0")) filterState = filterState.slice(0, -3);
      data.features = data.features.filter((d) => d.properties.NAME === filterState);
    }
    return data;
  },
  projection() {
    return this.filterState ? d3.geoMercator() : d3.geoAlbersUsa();
  },
  shortWideStates() {
    return [
      "Montana",
      "Nebraska",
      "North Dakota",
      "South Dakota",
      "Wyoming",
      "Oregon",
      "Kansas",
      "Colorado",
      "Tennessee",
      "Kentucky",
      "Virginia",
      "Pennsylvania",
      "Oklahoma",
      "North Carolina",
    ];
  },
  specialElections() {
    return this.results
      .filter(
        (r) =>
          r.name.includes("Special") && this.results.filter((item) => item.abb === r.abb).length > 1
      )
      .map((r) => r.abb);
  },
  specialElectionFeatures() {
    const specials = this.geojson.features.filter((feature) =>
      this.specialElections.includes(feature.properties.STUSPS)
    );
    const copiedSpecials = JSON.parse(JSON.stringify(specials));
    copiedSpecials.forEach((feature) => (feature.properties.isSpecial = true));

    return copiedSpecials;
  },
  insetStatesList() {
    return ["NH", "VT", "MA", "RI", "CT", "NJ", "DE", "MD", "DC"];
  },
  insetStates() {
    return this.geojson.features
      .filter((item) => this.insetStatesList.includes(item.properties.STUSPS))
      .sort(
        (a, b) =>
          this.insetStatesList.indexOf(a.properties.STUSPS) -
          this.insetStatesList.indexOf(b.properties.STUSPS)
      );
  },
  splitVoteStates() {
    const results = this.results.filter((r) => r.state.includes("01") || r.state.includes("02"));
    const splitVotes = [];
    results.forEach((s) => {
      let state = this.geojson.features.find((g) => g.properties.STUSPS === s.abb.substring(0, 2));
      state = JSON.parse(JSON.stringify(state));
      state.properties.STUSPS = s.abb;
      splitVotes.push(state);
    });
    return splitVotes;
  },
};

const methods = {
  ...mapActions(["setGeoSelection"]),
  getSelectedResults(d, isInset = false) {
    if (this.geoType === "state") {
      return this.results.find((item) => {
        if (d.properties.isSpecial) {
          return item.state === d.properties.NAME && item.electionType.includes("special");
        } else if (isInset === true && this.specialElections.includes(item.abb)) {
          return item.name.includes("Special") && item.state === d.properties.NAME;
        } else {
          return item.abb === d.properties.STUSPS;
        }
      });
    }
    if (this.geoType === "cd") {
      return this.results.find((item) => {
        if (this.isSplitVoteState) {
          const abb = this.results[0].abb.slice(0, -1);
          const state = this.results[0].state.slice(0, -3);
          const cd = this.results[0].state.replace(`${state} `, "");
          return abb === d.properties.STUSPS && parseInt(cd) === parseInt(d.properties.CD116FP);
        }
        // at-large districts
        if (["AK", "DE", "ND", "SD", "VT", "WY"].includes(item.abb)) {
          return item.abb === d.properties.STUSPS;
        } else {
          return (
            item.abb === d.properties.STUSPS &&
            parseInt(item.district) === parseInt(d.properties.CD116FP)
          );
        }
      });
    }
    if (this.geoType === "county") {
      return this.results.counties.find((item) => item.county === d.properties.NAME);
    }
    if (this.geoType === "vcu") {
      return this.results.vcus.find((item) => item.vcu === d.properties.NAME);
    }
    if (this.geoType === "precinct") {
      return this.results.precinctsData.find((item) => item.precinct === d.properties.Precinct);
    }
    if (this.geoType === "sldu") {
      return this.results.find((item) => item.district === d.properties.SLDUST);
    }
    if (this.geoType === "sldl") {
      return this.results.find((item) => item.district === d.properties.SLDLST);
    }
  },
  getWinnerStyle(item) {
    if (!item.winner) return;
    let hex = null;
    if (["Republican", "Democratic", "Green", "Libertarian"].includes(item.party_name)) {
      const partyName = capitalize(item.party_name);
      hex = getCSSVarHex(this.theme, this.colors.party[partyName]);
    } else {
      hex = getCSSVarHex(this.theme, this.colors.party.Other);
    }
    return `background-color: ${hexToRGB(hex, 0.2)} !important;`;
  },
  getFillColor(d, isSplitVote = false, isInset = false) {
    const self = this;
    const resultData = this.getSelectedResults(d, isSplitVote, isInset);
    if (!resultData) {
      return "#e0e0e0";
    }
    const winners = resultData.candidates.filter((c) => c.winner);
    const isGeneral = ["general", "special general"].includes(resultData.electionType);
    const hasMultipleWinners = winners.length > 1;
    const isRunoff = hasMultipleWinners && isGeneral;

    if (isRunoff) {
      const mapColorPatternsElement = this.$refs.mapColorPatterns;
      const mapColorPatterns = d3.select(mapColorPatternsElement);

      let pattern = mapColorPatterns
        .append("pattern")
        .attr("id", "runoff-pattern")
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 12)
        .attr("height", 12);

      pattern
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", 12)
        .attr("height", 12)
        .attr("fill", "#bdbdbd");

      pattern
        .append("rect")
        .attr("x", 0)
        .attr("height", 12)
        .attr("width", 4)
        .attr("fill", winners[0].color);

      pattern
        .append("rect")
        .attr("x", 8)
        .attr("height", 12)
        .attr("width", 4)
        .attr("fill", winners[1].color);

      return "url(#runoff-pattern)";
    }
    const leadCandidate = [...resultData.candidates].sort(
      (a, b) => b.winner - a.winner || b.percentage - a.percentage
    )[0];

    function createPattern(name, color) {
      const mapColorPatternsElement = self.$refs.mapColorPatterns;
      const mapColorPatterns = d3.select(mapColorPatternsElement);

      mapColorPatterns
        .append("pattern")
        .attr("id", `pattern${name}`)
        .attr("patternUnits", "userSpaceOnUse")
        .attr("patternTransform", "rotate(45 2 2)")
        .attr("width", 8)
        .attr("height", 8)
        .append("rect")
        .attr("width", 8)
        .attr("height", 8)
        .attr("x", 0)
        .attr("y", 0)
        .attr("fill", `${color}`)
        .attr("opacity", 0.8);

      d3.select(`#pattern${name}`)
        .append("path")
        .attr("d", "M 0 0 L 0 10")
        .attr("stroke", `${color}`)
        .attr("stroke-width", 10);
    }

    if (leadCandidate && leadCandidate.winning && !leadCandidate.winner) {
      if (["general", "special general"].includes(resultData.electionType)) {
        const colorId = `${leadCandidate.color.replace("var(--", "").replace(")", "")}`;
        const pattern = document.querySelector(`#pattern${colorId}`);
        if (!pattern) {
          createPattern(colorId, leadCandidate.color);
        }
        return `url(#pattern${colorId})`;
      } else {
        const pattern = document.querySelector(`#pattern${leadCandidate.cand_id}`);
        if (!pattern) {
          createPattern(leadCandidate.cand_id, leadCandidate.color);
        }

        return `url(#pattern${leadCandidate.cand_id})`;
      }
      // return leadCandidate.color
    } else if (leadCandidate && leadCandidate.winner) {
      return leadCandidate.color;
    }
    return "#bdbdbd";
  },
  resetSelected() {
    const mapFeaturesElement = this.$refs.mapFeatures;
    const mapFeatures = d3.select(mapFeaturesElement);

    mapFeatures
      .selectAll("path")
      .style("opacity", "1")
      .style("stroke", "#fff")
      .style("stroke-width", () => {
        if (this.geoType === "county") {
          return "0.75px";
        } else {
          return "0.5px";
        }
      });
  },
  highlightSelected(name, d3Obj) {
    this.resetSelected();

    this.selected = null;
    this.selected = name;

    d3.select(d3Obj).moveToFront().style("stroke", "#222").style("stroke-width", "1px");

    const mapFeaturesElement = this.$refs.mapFeatures;
    const mapFeatures = d3.select(mapFeaturesElement);

    mapFeatures.selectAll("path").each(function (d) {
      if (parseInt(d.properties.NAME) !== name) {
        d3.select(this).style("opacity", "0.5");
      } else {
        d3.select(this).style("opacity", "1");
      }
    });
  },
  drawMap() {
    this.drawFeatures();

    if (this.showSplitVote) this.drawSplitVote();

    if (this.showInsetStatesLegend) {
      this.drawInsets();
      this.drawLabels();
    }
  },
  drawFeatures() {
    const self = this;

    const mapFeaturesElement = self.$refs.mapFeatures;
    const mapFeatures = d3.select(mapFeaturesElement);

    mapFeatures
      .style("transform", `translate(0, ${this.insetStatesLegendHeight}px)`)
      .selectAll("path")
      .data(self.geojson.features)
      .enter()
      .append("path")
      .attr("class", self.filterState ? "cd" : "state")
      .attr("stroke", "#fff")
      .attr("d", self.path)
      .style("fill", (d) => {
        const fill = self.getFillColor(d);
        return fill !== null ? fill : "#bdbdbd";
      })
      .style("opacity", 0.9)
      .style("stroke-width", () => {
        if (self.geoType === "county") {
          return "0.75px";
        } else {
          return "0.5px";
        }
      })
      .on("click", (_event, feature) => self.onClick(feature))
      .on("mouseover", (_event, feature) => self.updateTooltipContent(feature))
      .on("mousemove", (event) => debounceAF(self.updateTooltipPosition(event)))
      .on("mouseout", () => self.hideTooltip());

    if (["state_house", "state_senate"].includes(self.office)) {
      mapFeatures.selectAll("path").each(function (d) {
        if (parseInt(d.properties.NAME) === self.geoSelection) {
          self.highlightSelected(self.geoSelection, this);
        }
      });
    }
  },
  getInsetStateLabelText(insetStateOrSpecialElection) {
    const stateAbbreviation = insetStateOrSpecialElection.properties.STUSPS;
    const isSpecial = insetStateOrSpecialElection.properties.isSpecial;

    return isSpecial ? stateAbbreviation + "*" : stateAbbreviation;
  },
  getInsetStateLabelTextColor(insetStateOrSpecialElection) {
    if (insetStateOrSpecialElection.properties.STUSPS === "HI") return "#222";
    const backgroundColor = this.getFillColor(insetStateOrSpecialElection, false, true);
    const backgroundColorIsLight = ["#bdbdbd", "#e0e0e0", "url(#runoff-pattern)"].includes(
      backgroundColor
    );
    const textColor = backgroundColorIsLight ? "#222" : "#fff";

    return textColor;
  },
  drawInsets() {
    const self = this;
    const spacing = 20;
    const insetWidth = 60;
    const insetStateAndSpecialElections = this.insetStates.concat(self.specialElectionFeatures);
    const mapInsetsElement = this.$refs.mapInsets;
    const mapInsets = d3.select(mapInsetsElement);
    const mapElement = self.$refs.map;
    const mapViewBox = mapElement.viewBox.baseVal;
    const mapWidth = mapViewBox.width;
    const mapCenter =
      mapWidth / 2 - (insetStateAndSpecialElections.length * insetWidth) / 2 - spacing * 2;

    mapInsets
      .selectAll(".state-inset")
      .data(insetStateAndSpecialElections)
      .enter()
      .append("rect")
      .attr("class", "state-inset")
      .attr("fill", (state) => self.getFillColor(state, false, true))
      .attr("width", insetWidth)
      .attr("height", insetWidth)
      .attr("x", (_d, i) => i * (insetWidth + spacing))
      .attr("y", 0)
      .style("transform", `translate(${mapCenter}px)`)
      .attr("rx", 6)
      .on("click", (_event, feature) => self.onClick(feature))
      .on("mouseover", (_event, feature) => self.updateTooltipContent(feature))
      .on("mousemove", (event) => debounceAF(self.updateTooltipPosition(event)))
      .on("mouseout", () => self.hideTooltip());

    const labelOffsetX = mapCenter + insetWidth / 2;
    const labelOffsetY = insetWidth / 2;

    mapInsets
      .selectAll(".inset-state-label")
      .data(insetStateAndSpecialElections)
      .enter()
      .append("text")
      .attr("class", "inset-state-label")
      .attr("fill", (state) => self.getInsetStateLabelTextColor(state))
      .text((state) => self.getInsetStateLabelText(state))
      .attr("x", (_d, i) => i * (insetWidth + spacing))
      .attr("y", 10)
      .style("transform", `translate(${labelOffsetX}px, ${labelOffsetY}px)`);
  },
  drawSplitVote() {
    const self = this;
    const mobile = window.innerWidth < 750;
    const spacing = 2;
    const insetWidth = window.innerWidth <= 420 ? 24 : 28;
    const boxWidth = insetWidth + 16;
    const yHeight = mobile
      ? self.container.clientHeight - insetWidth * 2 - 25
      : self.container.clientHeight - insetWidth * 2;
    const center =
      self.container.clientHeight / 2 - (self.splitVoteStates.length * insetWidth) / 2 + 20;
    this.gSplitVote = self.svg.append("g");

    const splitVoteStates = self.splitVoteStates.sort((a, b) => {
      if (mobile) {
        return a.properties.STUSPS.localeCompare(b.properties.STUSPS);
      }
      return b.properties.STUSPS.localeCompare(a.properties.STUSPS);
    });

    self.gSplitVote
      .selectAll("rect")
      .data(splitVoteStates)
      .enter()
      .append("g")
      .attr("class", "state-inset")
      .attr("transform", mobile ? `translate(0, ${center})` : null)
      .style("alignment-baseline", "central")
      .append("rect")
      .attr("width", boxWidth - spacing)
      .attr("height", insetWidth - spacing)
      .attr("y", (d, i) => {
        if (mobile) {
          return i * insetWidth;
        }
        return yHeight;
      })
      .attr("x", function (d, i) {
        if (mobile) {
          return `${self.container.clientWidth - insetWidth - spacing}px`;
        }
        return self.container.clientWidth - boxWidth - i * boxWidth;
      })
      .attr("fill", function (d) {
        const fill = self.getFillColor(d, true);
        return fill !== null ? fill : "#bdbdbd";
      })
      .style("opacity", 0.9)
      .on("click", (_event, feature) => self.onClick(feature))
      .on("mouseover", (_event, feature) => self.updateTooltipContent(feature))
      .on("mousemove", (event) => debounceAF(self.updateTooltipPosition(event)))
      .on("mouseout", () => self.hideTooltip());

    self.gSplitVote
      .selectAll(".state-inset")
      .append("text")
      .attr("transform", function (d, i) {
        if (mobile) {
          const x = self.container.clientWidth - insetWidth / 2 - spacing / 2;
          const y = insetWidth * i + insetWidth / 2 - spacing / 2;
          return `translate(${x}, ${y})`;
        } else {
          const y = yHeight + insetWidth / 2 - 1;
          const x = self.container.clientWidth - boxWidth * i - boxWidth / 2 - spacing / 2;
          return `translate(${x}, ${y})`;
        }
      })
      .style("text-anchor", "middle")
      .style("alignment-baseline", "central")
      .attr("fill", function (d) {
        return ["#bdbdbd", "#e0e0e0", "url(#runoff-pattern)"].includes(self.getFillColor(d))
          ? "#222"
          : "#fff";
      })
      .attr("font-size", mobile ? 9 : self.width * 0.0008 + "em")
      .attr("font-weight", 500)
      .style("opacity", 0.8)
      .style("pointer-events", "none")
      .text(function (d) {
        return d.properties.STUSPS;
      });
  },
  drawLabels() {
    const self = this;
    const mapLabelsElement = this.$refs.mapLabels;
    const mapLabels = d3.select(mapLabelsElement);

    const nonInsetStates = self.geojson.features.filter((item) => {
      const special =
        this.office === "senate" && this.specialElections.includes(item.properties.STUSPS);
      return (
        (!this.insetStatesList.includes(item.properties.STUSPS) &&
          !["PR"].includes(item.properties.STUSPS)) ||
        special
      );
    });

    mapLabels
      .style("transform", `translatey(${self.insetStatesLegendHeight}px)`)
      .selectAll("text")
      .data(nonInsetStates)
      .enter()
      .append("text")
      .attr("class", "map-label")
      .text((state) => self.getInsetStateLabelText(state))
      .attr("x", function (d) {
        if (self.path.centroid(d)[0]) {
          return self.path.centroid(d)[0];
        }
      })
      .attr("y", function (d) {
        if (self.path.centroid(d)[1]) {
          return self.path.centroid(d)[1];
        }
      })
      .attr("dx", function (d) {
        const abb = d.properties.STUSPS;
        if (self.labelAdjustments[abb] && self.labelAdjustments[abb].dx) {
          return self.labelAdjustments[abb].dx;
        } else {
          return "0";
        }
      })
      .attr("dy", function (d) {
        const abb = d.properties.STUSPS;
        if (self.labelAdjustments[abb] && self.labelAdjustments[abb].dy) {
          return self.labelAdjustments[abb].dy;
        } else {
          return "0.35em";
        }
      })
      .style("fill", (state) => self.getInsetStateLabelTextColor(state));
  },
  onClick(feature) {
    const result = this.getSelectedResults(feature);
    if (result) this.$emit("on-click", result);
  },
  setupMapPath() {
    const mapViewBox = this.mapElement.viewBox.baseVal;
    const mapWidth = mapViewBox.width;
    const mapHeight = mapViewBox.height - this.insetStatesLegendHeight;

    const projection = this.projection.fitSize([mapWidth, mapHeight], this.geojson);
    this.path = d3.geoPath(projection);
  },
};

const watch = {
  geoType() {
    this.drawMap();
  },
  loading() {
    if (!this.loading) {
      this.drawMap();
    }
  },
  filterState() {
    this.drawMap();
  },
  results() {
    this.$emit("change-key", Math.random());
    this.drawMap();
  },
};

export default {
  mixins,
  props,
  data: () => ({
    container: null,
    selected: null,
    insetWidth: null,
    labelAdjustments: {
      CA: { dx: "-1em" },
      FL: { dx: "1.2em" },
      ID: { dy: "1em" },
      LA: { dx: "-0.9em", dy: "-0.7em" },
      MI: { dx: "0.9em", dy: "2em" },
      MN: { dx: "-0.3em" },
      WV: { dx: "-0.05em" },
    },
    mapElement: null,
    map: null,
    path: null,
    showInsetStatesLegend: false,
    insetStatesLegendHeight: 0,
  }),
  computed,
  watch,
  beforeMount() {
    this.showInsetStatesLegend = this.geoType !== "cd";

    if (this.showInsetStatesLegend) this.insetStatesLegendHeight = 50;
  },
  mounted() {
    this.mapElement = this.$refs.map;
    this.map = d3.select(this.mapElement);
    this.setupMapPath();

    this.drawMap();
  },
  beforeUnmount() {
    this.hideTooltip();
  },
  methods,
};
</script>

<style>
.state-inset {
  cursor: pointer;
}

.map-label,
.inset-state-label {
  font-size: 1.5rem;
  font-weight: 500;
  opacity: 0.8;
  pointer-events: none;
  text-anchor: middle;
}

.ec-votes {
  font-weight: 400;
  opacity: 0.8;
  margin-left: 5px;
  font-size: 14px;
}

.candidates-table-row td {
  border-bottom: 1px solid #eeeeee;
  padding-top: 5px;
  padding-bottom: 3px;
  font-size: 13px !important;
}
</style>
